/**
 * packages/registry/src/define.ts — the field/collection spec of ARCHITECTURE.md §3.1.
 *
 * `defineCollection` + `field` are the ONLY way collection and field names are born
 * (CONSTRAINTS #5, ADR-005). Everything downstream (API, Sheets, UI, chat, scoping,
 * i18n) derives from the assembled registry by in-process import — no codegen.
 */
import { z } from "zod";

export type Tier = "hot" | "detail";

/**
 * PII scope tags (ARCHITECTURE §3.1 / §9). Default-deny: an UNTAGGED field is
 * owner/editor-only — forgetting a tag can only over-protect, never leak.
 * NOTE: the tag set below covers what the design docs name (crew, crew_self,
 * financial, promoter, public). `packages/scoping` (P3) owns the audience matrix;
 * extending this list is a registry change like any other.
 */
export const PII_TAGS = ["crew", "crew_self", "promoter", "public", "financial"] as const;
export type PiiTag = (typeof PII_TAGS)[number];

// Named tag constants: consumers (notably packages/scoping's audience matrix)
// import THESE instead of spelling the strings — the registry lint forbids
// collection-name lookalikes (e.g. "crew") as literals outside this package.
export const TAG_CREW = PII_TAGS[0];
export const TAG_CREW_SELF = PII_TAGS[1];
export const TAG_PROMOTER = PII_TAGS[2];
export const TAG_PUBLIC = PII_TAGS[3];
export const TAG_FINANCIAL = PII_TAGS[4];

/** Resolved scope of an untagged field: owner/editor-only (default-deny). */
export const OWNER_EDITOR_ONLY = "owner_editor" as const;
export type PiiScope = PiiTag | typeof OWNER_EDITOR_ONLY;

/** Declarative kind-gating (§3.1): server, UI and chat all read the same predicate. */
export interface VisibleWhen {
  readonly field: string;
  readonly in: readonly string[];
}

export interface SortSpec {
  readonly field: string;
  readonly dir?: "asc" | "desc"; // default asc
}

/**
 * Enum spec (§3.1): canonical stored values separated from display labels.
 * `open: true` (the default when this spec is given explicitly) means legacy free
 * text ("€€€", "shabbat (…)") still validates — canonical values feed chips,
 * Sheet dropdowns and chat tools, they are not a gate.
 */
export interface EnumSpec {
  readonly values: readonly string[];
  readonly open?: boolean;
}

export interface VariantOverride {
  readonly labelKey?: string;
  readonly placeholderKey?: string;
}

export interface FieldInput {
  /** zod schema — validation happens here, nowhere else (§3.1). */
  readonly type: z.ZodTypeAny;
  /** Wire/Sheet name; defaults to the field key. */
  readonly wireName?: string;
  /** Rename lineage — lossless op migration (CONSTRAINTS #6). */
  readonly renamedFrom?: readonly string[];
  /** hot | detail — progressive disclosure is registry data, not UI code. */
  readonly tier: Tier;
  /** FR/EN i18n key; must be `<collectionKey>.<something>`. */
  readonly labelKey: string;
  /** The chat-inference substrate ("kosher, halal, vegan…" lives HERE). */
  readonly description: string;
  /** PII scope tag. OMITTED = owner/editor-only (default-deny). */
  readonly pii?: PiiTag;
  /** Named Sheet column, or routed to the single explicit `extras` JSON column. */
  readonly sheet: { readonly column: string } | "extras";
  readonly visibleWhen?: VisibleWhen;
  /** Explicit enum spec; if omitted and `type` is a ZodEnum it is derived from it. */
  readonly enum?: EnumSpec;
  /** Suggested (never enforced) quick-entry chips. */
  readonly suggestedChips?: readonly string[];
  /** Per-kind label/placeholder overrides (§3.2 travel "seat"). */
  readonly variantOverrides?: Readonly<Record<string, VariantOverride>>;
  /**
   * Input-surface hint for kinds the zod shape cannot reveal (a phone, an
   * email and a timezone are all plain strings). Drives the P4 field
   * components (apps/web components/fields): keyboard, autocomplete, picker.
   */
  readonly input?: InputHint;
}

/** The behavioral input hints (see FieldInput.input). */
export type InputHint = "tel" | "email" | "tz" | "person";

const FIELD_BRAND = Symbol("td.registry.field");

export interface FieldSpec extends FieldInput {
  readonly [FIELD_BRAND]: true;
}

/** Wrap a field declaration. Purely declarative; resolution happens in defineCollection. */
export function field(input: FieldInput): FieldSpec {
  return { ...input, [FIELD_BRAND]: true };
}

/** Fully resolved field definition (defaults applied, enum derived, scope resolved). */
export interface FieldDef {
  readonly key: string;
  readonly type: z.ZodTypeAny;
  readonly wireName: string;
  readonly renamedFrom: readonly string[];
  readonly tier: Tier;
  readonly labelKey: string;
  readonly description: string;
  /** The raw tag; null = untagged. */
  readonly pii: PiiTag | null;
  /** Resolved scope: `pii` or OWNER_EDITOR_ONLY (default-deny). */
  readonly scope: PiiScope;
  readonly sheet: { readonly column: string } | "extras";
  readonly visibleWhen: VisibleWhen | null;
  readonly enum: { readonly values: readonly string[]; readonly open: boolean } | null;
  readonly suggestedChips: readonly string[];
  readonly variantOverrides: Readonly<Record<string, VariantOverride>>;
  /** Input-surface hint (tel/email/tz/person); null = plain text. */
  readonly input: InputHint | null;
}

export interface CollectionInput<K extends string, F extends Record<string, FieldSpec>> {
  /** The ONLY place this string is born. */
  readonly key: K;
  /** Must be exactly `collections.<key>` (ARCHITECTURE §3.2). */
  readonly labelKey: string;
  readonly icon?: string;
  /** Sheet tab name; identity is DeveloperMetadata `td_collection`, not the name. */
  readonly sheetTab: string;
  /** Field keys feeding the op `dedup_key` (content-level dedup, §4.1). */
  readonly dedupFields?: readonly (keyof F & string)[];
  /** Default list ordering. */
  readonly sort?: readonly SortSpec[];
  /**
   * Placement in the 4-split UI. NOTE: the canonical split names are pinned by the
   * P3 UI work (docs/design/APPROVED_PLAN.md P3); until then this is advisory data.
   */
  readonly split?: string;
  /**
   * Chat filing hint (§8.5, §11): tells the chatbot what belongs in this collection
   * and — crucially — what does NOT (e.g. schedule vs timetable).
   */
  readonly chatHint: string;
  readonly fields: F;
}

export interface CollectionDef<
  K extends string = string,
  F extends Record<string, FieldSpec> = Record<string, FieldSpec>,
> {
  readonly key: K;
  readonly labelKey: string;
  readonly icon: string | null;
  readonly sheetTab: string;
  readonly dedupFields: readonly string[];
  readonly sort: readonly SortSpec[];
  readonly split: string | null;
  readonly chatHint: string;
  /**
   * Statically keyed by the declared field names: `registry.travel.fields.gate`
   * is a FieldDef, not FieldDef|undefined — consumers never need `!` or `?.`
   * on declared fields (noUncheckedIndexedAccess stays strict for dynamic keys).
   */
  readonly fields: Readonly<{ [P in keyof F]: FieldDef }>;
  /** Declaration order — the canonical column/form order. */
  readonly fieldOrder: readonly string[];
}

/**
 * Widening accessor for DYNAMIC field lookup (consumers iterating unknown
 * keys). Statically-known fields should use `registry.<coll>.fields.<name>`
 * (typed non-optional); this is the one sanctioned way to index by string —
 * it returns `FieldDef | undefined` and the caller must handle the miss
 * (strict-validate-or-park, CONSTRAINTS #3).
 */
export function fieldsOf(def: CollectionDef): Readonly<Record<string, FieldDef | undefined>> {
  return def.fields as Readonly<Record<string, FieldDef | undefined>>;
}

export class RegistryDefinitionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RegistryDefinitionError";
  }
}

const COLLECTION_KEY_RE = /^[a-z][a-z0-9_]*$/;
const FIELD_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/;
const WIRE_NAME_RE = /^[a-z][a-z0-9_]*$/;

function unwrapType(t: z.ZodTypeAny): z.ZodTypeAny {
  let cur: z.ZodTypeAny = t;
  for (;;) {
    if (cur instanceof z.ZodOptional || cur instanceof z.ZodNullable) cur = cur.unwrap();
    else if (cur instanceof z.ZodDefault) cur = cur.removeDefault();
    else return cur;
  }
}

function deriveEnum(
  ckey: string,
  fkey: string,
  spec: FieldSpec,
): { values: readonly string[]; open: boolean } | null {
  const base = unwrapType(spec.type);
  const zodEnum = base instanceof z.ZodEnum ? (base.options as readonly string[]) : null;

  if (spec.enum) {
    const open = spec.enum.open ?? true;
    if (spec.enum.values.length === 0) {
      throw new RegistryDefinitionError(`${ckey}.${fkey}: enum.values must not be empty`);
    }
    for (const v of spec.enum.values) {
      if (!spec.type.safeParse(v).success) {
        throw new RegistryDefinitionError(
          `${ckey}.${fkey}: canonical enum value ${JSON.stringify(v)} does not validate against the zod type`,
        );
      }
    }
    if (!open) {
      if (!zodEnum) {
        throw new RegistryDefinitionError(
          `${ckey}.${fkey}: closed enum requires a z.enum type with the same canonical values`,
        );
      }
      const a = [...zodEnum].sort().join("|");
      const b = [...spec.enum.values].sort().join("|");
      if (a !== b) {
        throw new RegistryDefinitionError(
          `${ckey}.${fkey}: closed enum values [${b}] differ from the z.enum options [${a}]`,
        );
      }
    }
    return { values: [...spec.enum.values], open };
  }

  // Derived: a bare z.enum type is a closed enum with its options as canonical values.
  if (zodEnum) return { values: [...zodEnum], open: false };
  return null;
}

export function defineCollection<K extends string, F extends Record<string, FieldSpec>>(
  input: CollectionInput<K, F>,
): CollectionDef<K, F> {
  const ckey = input.key;
  if (!COLLECTION_KEY_RE.test(ckey)) {
    throw new RegistryDefinitionError(`collection key ${JSON.stringify(ckey)} must match ${COLLECTION_KEY_RE}`);
  }
  if (input.labelKey !== `collections.${ckey}`) {
    throw new RegistryDefinitionError(
      `${ckey}: collection labelKey must be "collections.${ckey}" (got ${JSON.stringify(input.labelKey)})`,
    );
  }
  if (!input.sheetTab.trim()) throw new RegistryDefinitionError(`${ckey}: sheetTab must not be empty`);
  if (!input.chatHint.trim()) throw new RegistryDefinitionError(`${ckey}: chatHint must not be empty`);

  const fieldKeys = Object.keys(input.fields);
  if (fieldKeys.length === 0) throw new RegistryDefinitionError(`${ckey}: a collection needs at least one field`);

  const fields: Record<string, FieldDef> = {};
  const seenWireNames = new Map<string, string>(); // wire or legacy name -> field key

  for (const fkey of fieldKeys) {
    const spec = input.fields[fkey]!;
    if (!FIELD_KEY_RE.test(fkey)) {
      throw new RegistryDefinitionError(`${ckey}.${fkey}: field key must match ${FIELD_KEY_RE}`);
    }
    if (!spec.description.trim()) {
      throw new RegistryDefinitionError(
        `${ckey}.${fkey}: description is required — it is the chat-inference substrate (§3.1)`,
      );
    }
    if (!spec.labelKey.startsWith(`${ckey}.`)) {
      throw new RegistryDefinitionError(
        `${ckey}.${fkey}: field labelKey must start with "${ckey}." (got ${JSON.stringify(spec.labelKey)})`,
      );
    }
    const wireName = spec.wireName ?? fkey;
    if (!WIRE_NAME_RE.test(wireName)) {
      throw new RegistryDefinitionError(`${ckey}.${fkey}: wireName ${JSON.stringify(wireName)} must match ${WIRE_NAME_RE}`);
    }
    for (const name of [wireName, ...(spec.renamedFrom ?? [])]) {
      const clash = seenWireNames.get(name);
      if (clash !== undefined) {
        throw new RegistryDefinitionError(
          `${ckey}: wire/legacy name ${JSON.stringify(name)} of field "${fkey}" collides with field "${clash}"`,
        );
      }
      seenWireNames.set(name, fkey);
    }
    for (const [variant, o] of Object.entries(spec.variantOverrides ?? {})) {
      for (const lk of [o.labelKey, o.placeholderKey]) {
        if (lk !== undefined && !lk.startsWith(`${ckey}.`)) {
          throw new RegistryDefinitionError(
            `${ckey}.${fkey}: variantOverrides.${variant} labelKey/placeholderKey must start with "${ckey}."`,
          );
        }
      }
    }

    fields[fkey] = Object.freeze({
      key: fkey,
      type: spec.type,
      wireName,
      renamedFrom: Object.freeze([...(spec.renamedFrom ?? [])]),
      tier: spec.tier,
      labelKey: spec.labelKey,
      description: spec.description,
      pii: spec.pii ?? null,
      scope: spec.pii ?? OWNER_EDITOR_ONLY,
      sheet: spec.sheet === "extras" ? "extras" : Object.freeze({ column: spec.sheet.column }),
      visibleWhen: spec.visibleWhen
        ? Object.freeze({ field: spec.visibleWhen.field, in: Object.freeze([...spec.visibleWhen.in]) })
        : null,
      enum: deriveEnum(ckey, fkey, spec),
      suggestedChips: Object.freeze([...(spec.suggestedChips ?? [])]),
      variantOverrides: Object.freeze({ ...(spec.variantOverrides ?? {}) }),
      input: spec.input ?? null,
    });
  }

  // Cross-field references must exist.
  for (const dk of input.dedupFields ?? []) {
    if (!(dk in fields)) throw new RegistryDefinitionError(`${ckey}: dedupFields references unknown field "${dk}"`);
  }
  for (const s of input.sort ?? []) {
    if (!(s.field in fields)) throw new RegistryDefinitionError(`${ckey}: sort references unknown field "${s.field}"`);
  }
  for (const [fkey, def] of Object.entries(fields)) {
    const vw = def.visibleWhen;
    if (!vw) continue;
    const target = fields[vw.field];
    if (!target) {
      throw new RegistryDefinitionError(`${ckey}.${fkey}: visibleWhen references unknown field "${vw.field}"`);
    }
    if (target.enum && !target.enum.open) {
      for (const v of vw.in) {
        if (!target.enum.values.includes(v)) {
          throw new RegistryDefinitionError(
            `${ckey}.${fkey}: visibleWhen value ${JSON.stringify(v)} is not a canonical value of "${vw.field}"`,
          );
        }
      }
    }
  }

  return Object.freeze({
    key: ckey,
    labelKey: input.labelKey,
    icon: input.icon ?? null,
    sheetTab: input.sheetTab,
    dedupFields: Object.freeze([...(input.dedupFields ?? [])]),
    sort: Object.freeze((input.sort ?? []).map((s) => Object.freeze({ field: s.field, dir: s.dir ?? "asc" }))),
    split: input.split ?? null,
    chatHint: input.chatHint,
    fields: Object.freeze(fields) as CollectionDef<K, F>["fields"],
    fieldOrder: Object.freeze(fieldKeys),
  });
}

/**
 * Assemble the registry object from collection defs. The result is the single
 * source of truth every consumer derives from (ADR-005).
 */
export function assembleRegistry<const T extends readonly CollectionDef<string>[]>(
  collections: T,
): { readonly [C in T[number] as C["key"]]: C } {
  const out: Record<string, CollectionDef> = {};
  const tabs = new Map<string, string>();
  for (const c of collections) {
    if (c.key in out) throw new RegistryDefinitionError(`duplicate collection key "${c.key}"`);
    const tabOwner = tabs.get(c.sheetTab);
    if (tabOwner !== undefined) {
      throw new RegistryDefinitionError(`sheetTab "${c.sheetTab}" of "${c.key}" collides with "${tabOwner}"`);
    }
    tabs.set(c.sheetTab, c.key);
    out[c.key] = c;
  }
  return Object.freeze(out) as { [C in T[number] as C["key"]]: C };
}
